import { Gauge, RefreshCw, ServerCog, TimerReset, Workflow } from "lucide-react";
import { api } from "../lib/api";
import { formatDate, formatNumber } from "../lib/format";
import type { Worker } from "../lib/types";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "../components/Ui";

export default function WorkersPage() {
  const { data, loading, error, refresh } = useResource((signal) => api.workers(signal), [], 5_000);
  const workers = data?.items || [];
  const live = workers.filter((worker) => workerState(worker) === "LIVE").length;
  const roles = new Set(workers.map((worker) => worker.role)).size;
  const inFlight = workers.reduce((sum, worker) => sum + Number(worker.in_flight ?? worker.active_leases ?? 0), 0);
  const protocolWorkers = workers.filter((worker) => isProtocolWorker(worker));
  const protocolAccounts = protocolWorkers.reduce((sum, worker) => sum + Number(worker.browser_accounts ?? 0), 0);
  const protocolAccountLimit = protocolWorkers.reduce((sum, worker) => sum + Number(worker.browser_account_limit ?? 0), 0);

  return (
    <div className="page-stack">
      <PageHeader title="Workers" description="租约、心跳和各处理阶段的实时进程" actions={<button className="button button-secondary" onClick={() => void refresh()}><RefreshCw size={16} />刷新</button>} />
      <section className="capacity-strip">
        <div><ServerCog size={17} /><span>在线进程</span><strong>{formatNumber(live)} / {formatNumber(workers.length)}</strong></div>
        <div><Gauge size={17} /><span>协议账号</span><strong>{formatNumber(protocolAccounts)} / {formatNumber(protocolAccountLimit)}</strong></div>
        <div><Workflow size={17} /><span>运行角色</span><strong>{formatNumber(roles)}</strong></div>
        <div><TimerReset size={17} /><span>报告中在途</span><strong>{formatNumber(inFlight)}</strong></div>
      </section>
      <section className="panel panel-flush">
        {loading && !data ? <LoadingState /> : error && !data ? <ErrorState error={error} onRetry={refresh} /> : !workers.length ? <EmptyState label="没有 worker 心跳" /> : (
          <div className="table-wrap"><table><thead><tr><th>Worker</th><th>角色</th><th>状态</th><th>并发 / 租约</th><th>主机 / PID</th><th>最后心跳</th><th>启动时间</th></tr></thead><tbody>{workers.map((worker) => (
            <tr key={worker.id || worker.worker_id}>
              <td><code>{worker.worker_id || worker.id}</code></td>
              <td><StatusBadge value={worker.role} /></td>
              <td><StatusBadge value={workerState(worker)} /></td>
              <td>{isProtocolWorker(worker) ? <div className="primary-cell"><strong>{formatNumber(worker.in_flight)} 个任务</strong><span>{formatNumber(worker.browser_accounts)} / {formatNumber(worker.browser_account_limit)} 个账号</span></div> : <div className="slot-count"><strong>{formatNumber(worker.active_leases)}</strong><span>/ {formatNumber(worker.concurrency, "-")}</span></div>}</td>
              <td><div className="primary-cell"><span>{worker.hostname || "-"}</span><small>PID {worker.pid || "-"}</small></div></td>
              <td>{formatDate(worker.last_heartbeat_at)}</td>
              <td>{formatDate(worker.started_at)}</td>
            </tr>
          ))}</tbody></table></div>
        )}
      </section>
    </div>
  );
}

function workerState(worker: Worker): string {
  if (worker.status) return worker.status;
  if (!worker.last_heartbeat_at) return "STALE";
  const raw = worker.last_heartbeat_at;
  const numeric = typeof raw === "number" || /^\d+(\.\d+)?$/.test(String(raw)) ? Number(raw) : null;
  const time = numeric !== null ? (numeric < 10_000_000_000 ? numeric * 1000 : numeric) : new Date(raw).getTime();
  return Date.now() - time < 60_000 ? "LIVE" : "STALE";
}

function isProtocolWorker(worker: Worker): boolean {
  return ["protocol", "browser"].includes(String(worker.role).toLowerCase());
}
